import React, { useEffect, useMemo, useState } from "react";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Divider } from "primereact/divider";
import { Message } from "primereact/message";
import { Toast } from "primereact/toast";
import { isValid } from "date-fns";
import { AxiosError } from "axios";
import type { BannerSwap, ChannelSettings } from "../../../utils/@types";
import { apiService } from "../../../api/api";
import { bannerDashboardApi } from "../../../api/bannerDashboardApi";
import {
  formatSwapDateCell,
  scheduleOverlapsPeers,
  toBannerSwapDate,
  isPermanentSwapEnd,
} from "../bannerDashboardUtils";
import "../bannerSwapDialogs.css";

type Props = {
  rowData: BannerSwap;
  bannerSwapQueue: BannerSwap[];
  visible: boolean;
  onHide: () => void;
  onSaved: () => Promise<void>;
  toast: React.RefObject<Toast | null>;
};

function formatBrandLabel(brand: ChannelSettings["Brand"]): string {
  if (!brand) {
    return "—";
  }
  return Array.isArray(brand) ? brand.join(", ") : String(brand);
}

function CellText({
  text,
  multiline = false,
}: {
  text: string;
  multiline?: boolean;
}) {
  return (
    <span
      className={
        multiline
          ? "banner-swap-edit-end-dialog__cell-text banner-swap-edit-end-dialog__cell-text--multiline"
          : "banner-swap-edit-end-dialog__cell-text"
      }
      title={text}
    >
      {text}
    </span>
  );
}

const EditRunningSwapEndDateDialog: React.FC<Props> = ({
  rowData,
  bannerSwapQueue,
  visible,
  onHide,
  onSaved,
  toast,
}) => {
  const [editEnd, setEditEnd] = useState<Date>(new Date());
  const [initialEndMs, setInitialEndMs] = useState<number>(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [channelsLoading, setChannelsLoading] = useState(false);
  const [channelsError, setChannelsError] = useState<string | null>(null);
  const [channelDetails, setChannelDetails] = useState<ChannelSettings[]>([]);
  const [markedForRemoval, setMarkedForRemoval] = useState<Set<string>>(
    () => new Set()
  );

  const swapIsPermanent = useMemo(() => {
    const endDate = toBannerSwapDate(rowData.endDate);
    return isValid(endDate) && isPermanentSwapEnd(endDate);
  }, [rowData.endDate]);

  useEffect(() => {
    if (!visible) {
      return;
    }
    setError(null);
    setChannelsError(null);
    setMarkedForRemoval(new Set());
    const startD = toBannerSwapDate(rowData.startDate);
    const endDate = toBannerSwapDate(rowData.endDate);
    if (!isValid(startD) || !isValid(endDate)) {
      setError("Could not read this swap's schedule. Close and refresh the queue.");
      return;
    }
    if (!isPermanentSwapEnd(endDate)) {
      setInitialEndMs(endDate.getTime());
      setEditEnd(new Date(endDate.getTime()));
    }
  }, [visible, rowData.startDate, rowData.endDate, rowData.swapUID]);

  useEffect(() => {
    if (!visible || !rowData.channelIDs?.length) {
      setChannelDetails([]);
      return;
    }
    let cancelled = false;
    const loadChannels = async () => {
      setChannelsLoading(true);
      setChannelsError(null);
      try {
        const allSettings =
          await apiService.channelSettings.getAllChannelSettings();
        if (cancelled) {
          return;
        }
        const idList = rowData.channelIDs.map(String);
        const seen = new Set<string>();
        const details: ChannelSettings[] = [];
        for (const id of idList) {
          const row = allSettings.find(
            (c) => c.ChannelId === id || (c as { id?: string }).id === id
          );
          if (row && !seen.has(row.ChannelId)) {
            seen.add(row.ChannelId);
            details.push(row);
          }
        }
        setChannelDetails(details);
      } catch {
        if (!cancelled) {
          setChannelsError(
            "Could not load channel details. You can still edit the end date."
          );
          setChannelDetails([]);
        }
      } finally {
        if (!cancelled) {
          setChannelsLoading(false);
        }
      }
    };
    void loadChannels();
    return () => {
      cancelled = true;
    };
  }, [visible, rowData.channelIDs]);

  const startDate = toBannerSwapDate(rowData.startDate);
  const minEndDate = isValid(startDate) ? startDate : new Date();

  const endDateChanged = useMemo(() => {
    if (swapIsPermanent) {
      return false;
    }
    if (!isValid(editEnd)) {
      return false;
    }
    return editEnd.getTime() !== initialEndMs;
  }, [swapIsPermanent, editEnd, initialEndMs]);

  const hasRemovals = markedForRemoval.size > 0;
  const hasChanges = endDateChanged || hasRemovals;

  const remainingChannelIds = useMemo(
    () =>
      rowData.channelIDs.filter((id) => !markedForRemoval.has(String(id))),
    [rowData.channelIDs, markedForRemoval]
  );

  const toggleMarkForRemoval = (channelId: string) => {
    if (saving) {
      return;
    }
    setError(null);
    setMarkedForRemoval((prev) => {
      const next = new Set(prev);
      if (next.has(channelId)) {
        next.delete(channelId);
      } else {
        next.add(channelId);
      }
      return next;
    });
  };

  const validateEndDate = (channelIdsForOverlap: string[]): string | null => {
    if (rowData.status !== "running") {
      return "Only running swaps can have their end date edited.";
    }
    if (!isValid(editEnd)) {
      return "End date and time are invalid.";
    }
    if (isValid(startDate) && editEnd.getTime() <= startDate.getTime()) {
      return "End must be after the swap start date.";
    }
    if (editEnd.getTime() <= Date.now()) {
      return "Choose a future end date, or use End now to finish the swap immediately.";
    }
    if (isValid(startDate) && channelIdsForOverlap.length > 0) {
      const peerOverlaps = scheduleOverlapsPeers(
        channelIdsForOverlap,
        startDate,
        editEnd,
        rowData.swapUID,
        bannerSwapQueue,
        rowData.channelSchedules
      );
      if (peerOverlaps.length > 0) {
        return `The new end date overlaps existing swap(s): ${peerOverlaps.join(", ")}`;
      }
    }
    return null;
  };

  const handleSave = async () => {
    if (!hasChanges) {
      setError(
        swapIsPermanent
          ? "No changes to save. Mark channels to remove, or use End now."
          : "No changes to save. Update the end date or mark channels to remove."
      );
      return;
    }

    const toRemove = Array.from(markedForRemoval);
    const willCancelSwap =
      toRemove.length > 0 && remainingChannelIds.length === 0;

    if (!willCancelSwap && endDateChanged) {
      const validationError = validateEndDate(remainingChannelIds);
      if (validationError) {
        setError(validationError);
        return;
      }
    }

    setSaving(true);
    setError(null);

    let channelsRemoved = false;

    try {
      let swapCancelled = false;

      if (toRemove.length > 0) {
        const removeResult = await bannerDashboardApi.patchRemoveChannelsFromSwap(
          rowData.swapUID,
          toRemove
        );
        channelsRemoved = true;
        swapCancelled = removeResult.swapCancelled;
      }

      if (!swapCancelled && endDateChanged && remainingChannelIds.length > 0) {
        await bannerDashboardApi.patchBannerSwapRunningEndDate(
          rowData.swapUID,
          editEnd
        );
      }

      const parts: string[] = [];
      if (toRemove.length > 0) {
        parts.push(
          swapCancelled
            ? "Swap cancelled and removed channel(s) reverted to default."
            : `${toRemove.length} channel(s) removed and reverted to default.`
        );
      }
      if (!swapCancelled && endDateChanged) {
        parts.push("End date updated.");
      }

      toast.current?.show({
        severity: "success",
        summary: swapCancelled ? "Swap cancelled" : "Swap updated",
        detail: parts.join(" ") || "Changes saved.",
        life: 5000,
      });

      await onSaved();
    } catch (err) {
      const ax = err as AxiosError<{ message?: string }>;
      let msg =
        ax.response?.data?.message ??
        "Failed to save changes. Nothing was applied — try again.";
      if (channelsRemoved && endDateChanged) {
        msg = `${msg} Channel removals were saved; refresh the queue and retry the end date if needed.`;
      }
      setError(msg);
      toast.current?.show({
        severity: "error",
        summary: "Save failed",
        detail: msg,
        life: 8000,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleEndNow = async () => {
    setSaving(true);
    setError(null);
    try {
      await bannerDashboardApi.endBannerSwapNow(rowData.swapUID);
      toast.current?.show({
        severity: "success",
        summary: "Swap ending",
        detail: "All channels will revert to their default banners now.",
        life: 5000,
      });
      await onSaved();
    } catch (err) {
      const ax = err as AxiosError<{ message?: string }>;
      const msg = ax.response?.data?.message ?? "Failed to end swap.";
      setError(msg);
      toast.current?.show({
        severity: "error",
        summary: "End now failed",
        detail: msg,
        life: 8000,
      });
    } finally {
      setSaving(false);
    }
  };

  const actionBody = (ch: ChannelSettings) => {
    const marked = markedForRemoval.has(ch.ChannelId);
    return (
      <div className="banner-swap-edit-end-dialog__action-cell">
        <Button
          icon={marked ? "pi pi-undo" : "pi pi-times"}
          severity={marked ? "secondary" : "danger"}
          rounded
          text
          size="small"
          disabled={saving}
          aria-label={marked ? `Undo removal of ${ch.ChannelTitle || ch.ChannelId}` : `Remove ${ch.ChannelTitle || ch.ChannelId}`}
          tooltip={marked ? "Undo removal" : "Remove channel"}
          tooltipOptions={{ position: "left" }}
          onClick={() => toggleMarkForRemoval(ch.ChannelId)}
        />
      </div>
    );
  };

  const footer = (
    <div className="banner-swap-edit-end-dialog__footer">
      <Button
        label="End now"
        icon="pi pi-stop-circle"
        severity="danger"
        outlined
        disabled={saving}
        onClick={() => void handleEndNow()}
      />
      <div className="flex flex-wrap gap-2">
        <Button
          label="Cancel"
          severity="secondary"
          disabled={saving}
          onClick={onHide}
        />
        <Button
          label="Save"
          icon={saving ? "pi pi-spin pi-spinner" : "pi pi-check"}
          loading={saving}
          disabled={saving || !hasChanges}
          onClick={() => void handleSave()}
        />
      </div>
    </div>
  );

  return (
    <Dialog
      className="banner-swap-edit-end-dialog"
      header="Edit end date"
      visible={visible}
      footer={footer}
      onHide={() => !saving && onHide()}
      closable={!saving}
      blockScroll
    >
      <div className="banner-swap-edit-end-dialog__body">
        {error && (
          <Message severity="error" text={error} className="w-full m-0" />
        )}

        {swapIsPermanent && (
          <Message
            severity="info"
            className="w-full m-0"
            text="This swap has no automatic end — the brand banner stays live until you click End now or remove channels below."
          />
        )}

        <section className="banner-swap-edit-end-dialog__schedule">
          <p className="banner-swap-edit-end-dialog__intro m-0">
            Started{" "}
            <span className="font-medium">
              {formatSwapDateCell(rowData.startDate)}
            </span>
            .{" "}
            {swapIsPermanent
              ? "Remove channels below or use End now to revert banners."
              : "Choose when the swap should end, then save."}
          </p>

          {!swapIsPermanent && (
            <div className="field m-0">
              <label
                htmlFor="runningEditEnd"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                End date and time
              </label>
              <Calendar
                id="runningEditEnd"
                value={isValid(editEnd) ? editEnd : null}
                onChange={(e) => {
                  const v = e.value;
                  if (v instanceof Date && isValid(v)) {
                    setEditEnd(new Date(v.getTime()));
                    setError(null);
                  }
                }}
                showTime
                hourFormat="24"
                showIcon
                className="banner-swap-edit-end-dialog__calendar w-full"
                inputClassName="w-full"
                minDate={minEndDate}
                disabled={saving}
              />
            </div>
          )}
        </section>

        <Divider className="my-0" />

        <section className="banner-swap-edit-end-dialog__channels">
          <div className="banner-swap-edit-end-dialog__channels-header">
            <h4 className="text-sm font-semibold text-gray-900 m-0">
              Channels in this swap ({rowData.channelIDs.length})
            </h4>
            {hasRemovals && (
              <span className="text-xs text-amber-700 font-medium shrink-0">
                {markedForRemoval.size} marked for removal
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 m-0">
            Mark channels to remove from this swap. Changes apply when you click
            Save — running swaps revert removed channels to their default banner.
          </p>

          {channelsLoading ? (
            <p className="text-sm text-gray-500 m-0">Loading channels…</p>
          ) : channelsError ? (
            <Message severity="warn" text={channelsError} className="w-full m-0" />
          ) : channelDetails.length === 0 ? (
            <p className="text-sm text-gray-500 m-0">No channels on this swap.</p>
          ) : (
            <DataTable
              value={channelDetails}
              dataKey="ChannelId"
              size="small"
              showGridlines
              scrollable
              scrollHeight="320px"
              className="banner-swap-edit-end-dialog__table"
              rowClassName={(row: ChannelSettings) =>
                markedForRemoval.has(row.ChannelId)
                  ? "banner-swap-edit-end-dialog__row--marked"
                  : ""
              }
              emptyMessage="No channels on this swap."
            >
              <Column
                header="Channel"
                body={(ch: ChannelSettings) => (
                  <CellText text={ch.ChannelTitle || "—"} />
                )}
                style={{ minWidth: "11rem", maxWidth: "16rem" }}
              />
              <Column
                header="Brand"
                body={(ch: ChannelSettings) => (
                  <CellText text={formatBrandLabel(ch.Brand)} multiline />
                )}
                style={{ minWidth: "9rem", maxWidth: "14rem" }}
              />
              <Column
                header="Channel ID"
                body={(ch: ChannelSettings) => (
                  <CellText
                    text={ch.ChannelId}
                    multiline={false}
                  />
                )}
                bodyClassName="font-mono text-xs"
                style={{ minWidth: "10rem", maxWidth: "12rem" }}
              />
              <Column
                header="Remove"
                body={actionBody}
                align="center"
                style={{ width: "4.5rem" }}
                frozen
                alignFrozen="right"
              />
            </DataTable>
          )}

          {remainingChannelIds.length === 0 && hasRemovals && (
            <Message
              severity="warn"
              text="Removing all channels will cancel this swap when you save."
              className="w-full m-0"
            />
          )}
        </section>
      </div>
    </Dialog>
  );
};

export default EditRunningSwapEndDateDialog;
